import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';
import { morphSection } from '@theme/section-renderer';
import { morph } from '@theme/morph';

/**
 * Horizon overrides for Shopify.actions:
 * - updateCart: emit events from the cart drawer scope.
 * - openCart: open the cart drawer (fall back to /cart when absent).
 */

function init() {
  const actions = window.Shopify?.actions;
  if (!actions) return;

  actions.updateCart.configure({
    eventTarget: () => document.querySelector('theme-drawer#cart-drawer') ?? document,
  });

  actions.openCart.configure({
    async handler() {
      /** @type {HTMLElement & {open?: () => void} | null} */
      const drawer = document.querySelector('theme-drawer#cart-drawer');

      if (drawer?.open) {
        drawer.open();
      } else {
        window.location.href = Theme.routes.cart_url || '/cart';
      }
    },
  });
}

function formatCartMoney(cents, currency) {
  return new Intl.NumberFormat(document.documentElement.lang || 'sv-SE', {
    style: 'currency',
    currency: currency || window.Shopify?.currency?.active || 'SEK',
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

function syncFreeShippingCounter(cart) {
  const counter = document.querySelector('#cart-drawer .belyr-caia-cart-shipping');
  if (!(counter instanceof HTMLElement)) return;

  const threshold = Number(counter.dataset.freeShippingThreshold) || 30000;
  const total = Number(cart?.total_price);
  if (!Number.isFinite(total)) return;

  const remaining = Math.max(threshold - total, 0);
  const prefix = counter.dataset.freeShippingPrefix?.trim();
  const suffix = counter.dataset.freeShippingSuffix?.trim() || 'kvar till fraktfritt!';
  const success = counter.dataset.freeShippingSuccess?.trim() || 'Din order är nu fraktfri!';

  if (remaining === 0) {
    counter.textContent = success;
    return;
  }

  const amount = document.createElement('strong');
  const suffixText = document.createElement('span');
  amount.className = 'belyr-caia-cart-shipping__amount';
  amount.textContent = formatCartMoney(remaining, cart.currency);
  suffixText.textContent = suffix;

  const content = [amount, suffixText];
  if (prefix) {
    const prefixText = document.createElement('span');
    prefixText.textContent = prefix;
    content.unshift(prefixText);
  }

  counter.replaceChildren(...content);
}

function patchCartSection(sectionId, sectionHtml) {
  const currentSection = document.getElementById(`shopify-section-${sectionId}`);
  if (!currentSection) return false;

  const incomingDocument = new DOMParser().parseFromString(sectionHtml, 'text/html');
  const incomingSection = incomingDocument.getElementById(`shopify-section-${sectionId}`) || incomingDocument;
  const currentCart = currentSection.querySelector('cart-items-component');
  const incomingCart = incomingSection.querySelector('cart-items-component');
  if (!currentCart || !incomingCart) return false;

  const currentEmpty = currentCart.querySelector('[data-cart-drawer-empty]') !== null;
  const incomingEmpty = incomingCart.querySelector('[data-cart-drawer-empty]') !== null;
  if (currentEmpty !== incomingEmpty) return false;

  const selectors = [
    '.cart-items__wrapper',
    '.cart-drawer__upsells',
    '.belyr-caia-cart-total',
    '.belyr-caia-cart-shipping',
  ];
  const pairs = [
    [currentSection.querySelector('#cart-drawer-heading'), incomingSection.querySelector('#cart-drawer-heading')],
    ...selectors.map((selector) => [
      currentCart.querySelector(selector),
      incomingCart.querySelector(selector),
    ]),
  ];

  if (pairs.some(([current, incoming]) => Boolean(current) !== Boolean(incoming))) return false;

  for (const [current, incoming] of pairs) {
    if (!current || !incoming) continue;

    // Recreate the carousel after its products change. Morphing its children
    // leaves the custom element pointing at the old track and can freeze the
    // next product image after an upsell is added.
    if (current.matches('belyr-cart-carousel.cart-drawer__upsells')) {
      const replacement = incoming.cloneNode(true);
      replacement.removeAttribute('data-ready');
      current.replaceWith(replacement);
      continue;
    }

    morph(current, incoming);
  }

  return true;
}

function showPendingCartLine(form) {
  if (form.closest('.belyr-cart-upsell')) return () => {};

  const items = document.querySelector('#cart-drawer .cart-drawer__items');
  if (!items) return () => {};

  const context = form.closest(
    '.belyr-cart-upsell, product-card, article, .product-information, .product-details'
  ) || form.parentElement;
  const sourceImage = context?.querySelector('img');
  const titleElement = context?.querySelector(
    '.belyr-cart-upsell__copy a, .product-card__title, [data-product-title], h1, h2, h3'
  );
  const imageUrl = sourceImage?.currentSrc || sourceImage?.src || '';
  const title = titleElement?.textContent?.trim() || sourceImage?.alt?.trim() || '';
  const pending = document.createElement('div');
  const media = document.createElement('span');
  const copy = document.createElement('span');
  const activity = document.createElement('span');

  pending.className = 'belyr-cart-pending-line';
  pending.setAttribute('aria-hidden', 'true');
  media.className = 'belyr-cart-pending-line__media';
  copy.className = 'belyr-cart-pending-line__copy';
  activity.className = 'belyr-cart-pending-line__activity';

  if (imageUrl) {
    const image = document.createElement('img');
    image.src = imageUrl;
    image.alt = '';
    media.append(image);
  } else {
    media.classList.add('belyr-cart-pending-line__skeleton');
  }

  if (title) {
    const productTitle = document.createElement('strong');
    productTitle.textContent = title;
    copy.append(productTitle);
  }

  const detailLine = document.createElement('span');
  detailLine.className = 'belyr-cart-pending-line__skeleton';
  copy.append(detailLine);
  pending.append(media, copy, activity);
  items.prepend(pending);
  items.scrollTop = 0;

  return () => pending.remove();
}

async function handleFastProductForm(event) {
  if (!(event.target instanceof HTMLFormElement)) return;

  const form = event.target;
  const productForm = form.closest('product-form-component');
  const isProductPageForm = form.matches('.belyr-pdp__form');
  const isThemeProductForm = productForm && form.matches('[data-type="add-to-cart-form"]');

  if (!isProductPageForm && !isThemeProductForm) return;

  event.preventDefault();
  event.stopImmediatePropagation();

  if (form.dataset.belyrSubmitting === 'true') return;
  form.dataset.belyrSubmitting = 'true';

  const submitButton = event.submitter instanceof HTMLButtonElement ? event.submitter : form.querySelector('[type="submit"]');
  const formData = new FormData(form);
  const variantId = String(formData.get('id') || '');
  const quantity = Math.max(1, Number(formData.get('quantity')) || 1);
  const source = productForm || form;
  const sectionIds = Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
    .map((component) => component.dataset.sectionId)
    .filter(Boolean);

  if (!variantId) {
    form.dataset.belyrSubmitting = 'false';
    return;
  }

  formData.set('sections', Array.from(new Set(sectionIds.length ? sectionIds : ['cart-drawer-section'])).join(','));
  formData.set('sections_url', window.location.pathname);

  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: isProductPageForm ? 'product' : 'dialog',
      lines: [{ merchandiseId: variantId, quantity }],
      promise: deferred.promise,
    })
  );

  if (submitButton) {
    submitButton.setAttribute('aria-busy', 'true');
  }

  const drawer = document.querySelector('theme-drawer#cart-drawer');
  if (drawer && typeof drawer.open === 'function' && !drawer.isOpen) drawer.open();
  const clearPendingCartLine = showPendingCartLine(form);

  try {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: formData,
    });
    const result = await response.json();

    if (!response.ok || result.status) {
      throw new Error(result.description || result.message || 'Add to cart failed');
    }

    const renderedSections = Object.entries(result.sections || {}).filter(([, html]) => typeof html === 'string');
    const cartPromise = fetch(`${root}cart.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    }).then((cartResponse) => {
      if (!cartResponse.ok) throw new Error('Failed to synchronize cart');
      return cartResponse.json();
    });

    await Promise.all(
      renderedSections.map(([sectionId, html]) => {
        if (patchCartSection(sectionId, html)) return Promise.resolve();
        return morphSection(sectionId, html, { mode: 'hydration' });
      })
    );

    cartPromise
      .then((ajaxCart) => {
        syncFreeShippingCounter(ajaxCart);
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
          detail: {
            source: renderedSections.length ? 'belyr-fast-product-form' : 'belyr-fast-product-form-fallback',
            items: ajaxCart.items,
            itemCount: ajaxCart.item_count,
            productId: productForm?.dataset.productId,
            sections: result.sections,
            didError: false,
          },
        });
      })
      .catch(deferred.reject);

    if (submitButton) {
      submitButton.dataset.added = 'true';
      window.setTimeout(() => submitButton.removeAttribute('data-added'), 500);
    }

  } catch (error) {
    deferred.reject(error);
    source.dispatchEvent(
      new CartErrorEvent({
        error: error?.message || 'Add to cart failed',
        code: 'SERVICE_UNAVAILABLE',
      })
    );
  } finally {
    clearPendingCartLine();
    form.dataset.belyrSubmitting = 'false';

    if (submitButton) {
      submitButton.removeAttribute('aria-busy');
    }
  }
}

function disableCartUpsellFlyToCart(event) {
  if (!(event.target instanceof Element)) return;

  const addToCart = event.target.closest(
    '#cart-drawer .belyr-cart-upsell add-to-cart-component'
  );

  if (addToCart instanceof HTMLElement) {
    addToCart.dataset.addToCartAnimation = 'false';
  }
}

document.addEventListener('click', disableCartUpsellFlyToCart, true);
document.addEventListener('submit', handleFastProductForm, true);

// Run immediately if the standard-actions bundle has already attached
// `Shopify.actions`; otherwise wait for DOMContentLoaded, which fires after
// all module scripts have executed regardless of document order.
if (window.Shopify?.actions) {
  init();
} else {
  document.addEventListener('DOMContentLoaded', init, { once: true });
}
